import { LscApiError } from './api';
import { displaySeasonLabel } from './seasonDisplay';
import type {
  LscLeague,
  LscSupportIssueCategory,
  LscSupportIssueContext,
  LscSupportIssueSeverity,
  LscSupportIssueStructuredErrorCode,
} from './types';
import { LSC_SUPPORT_ISSUE_STRUCTURED_ERROR_CODES } from './types';
import { buildInternalCopyPattern } from './internalSanitizerPatterns';

export const REPORT_ISSUE_MODAL_TITLE = 'Report an issue';
export const REPORT_ISSUE_MODAL_INTRO =
  'Tell us what went wrong. Your report helps our team investigate — it does not change league data, billing, or schedule settings.';
export const REPORT_ISSUE_SUBMIT_CTA = 'Submit report';
export const REPORT_ISSUE_SUCCESS_TITLE = 'Report received';
export const REPORT_ISSUE_SUCCESS_COPY =
  'Thanks — we received your report and will follow up if we need more details.';
export const REPORT_ISSUE_ATTACHMENTS_UNSUPPORTED_COPY =
  'Screenshot uploads are not supported yet. Describe what you saw instead.';
export const REPORT_ISSUE_GLOBAL_LINK_LABEL = 'Report an issue';
export const REPORT_ISSUE_CONTEXTUAL_LINK_LABEL = 'Report this issue';

const CATEGORY_LABELS: Record<LscSupportIssueCategory, string> = {
  billing: 'Billing or subscription',
  setup: 'League setup',
  schedule: 'Schedule sync or repair',
  live_truth: 'Live verification',
  vod_handoff: 'VOD handoff',
  archive_review: 'Archive review',
  login_access: 'Sign-in or access',
  performance: 'Performance or loading',
  mobile_pwa: 'Mobile app or install',
  reel_failed: 'Reel failed or missing',
  lost_credit: 'Lost a credit or free reel',
  quality: 'Reel quality',
  other: 'Something else',
};

const SEVERITY_LABELS: Record<LscSupportIssueSeverity, string> = {
  low: 'Low — minor inconvenience',
  medium: 'Medium — blocking my workflow',
  high: 'High — major problem',
  launch_blocker: 'Launch blocker — cannot proceed',
};

const SUPPORT_ISSUE_ERROR_COPY: Record<LscSupportIssueStructuredErrorCode, string> = {
  issue_invalid_category: 'Choose a valid issue category and try again.',
  issue_invalid_status: 'This issue could not be updated. Refresh and try again.',
  issue_invalid_severity: 'Choose a valid severity and try again.',
  issue_context_too_large:
    'Too much diagnostic detail was included. Shorten your description and try again.',
  issue_attachment_not_supported: REPORT_ISSUE_ATTACHMENTS_UNSUPPORTED_COPY,
  issue_forbidden: 'You do not have permission to submit this report.',
  issue_not_found: 'This report could not be found. It may have been removed.',
  issue_rate_limited: 'You have submitted several reports recently. Please wait a moment and try again.',
  admin_forbidden: 'This action is only available to Scoutlytics support staff.',
  admin_read_only: 'Support tools are in read-only mode right now.',
};

const INTERNAL_COPY_PATTERN = buildInternalCopyPattern(
  'firestore|cloud run|webhook|stripe payload|oauth|api_key|secret|token|operator|env_|__ENV_PREFIX__|raw payload|session cookie'
);

const FORBIDDEN_CONTEXT_KEY_PATTERN =
  /token|secret|password|cookie|authorization|oauth|stripe|webhook|api_key|session|bearer|credential/i;

const SAFE_CONTEXT_KEYS = new Set([
  'route',
  'frontend_bundle',
  'backend_health_sha',
  'league_active_season',
  'league_active_week',
  'league_phase',
  'billing_posture',
  'correlation_id',
  'error_code',
  'surface',
  'browser_family',
  'timestamp',
  'job_id',
  'tier',
  'reel_state',
  'outcome',
  'funding',
]);

export function supportIssueCategoryLabel(category: LscSupportIssueCategory): string {
  return CATEGORY_LABELS[category];
}

export function supportIssueSeverityLabel(severity: LscSupportIssueSeverity): string {
  return SEVERITY_LABELS[severity];
}

export function assertCustomerSafeSupportIssueCopy(text: string): boolean {
  return !INTERNAL_COPY_PATTERN.test(text);
}

export function supportIssueErrorMessage(
  code: string | undefined,
  fallbackMessage?: string
): string {
  if (code && code in SUPPORT_ISSUE_ERROR_COPY) {
    return SUPPORT_ISSUE_ERROR_COPY[code as LscSupportIssueStructuredErrorCode];
  }
  if (fallbackMessage && !INTERNAL_COPY_PATTERN.test(fallbackMessage)) {
    return fallbackMessage;
  }
  return 'Your report could not be sent. Check your connection and try again.';
}

export function parseSupportIssueError(err: unknown, fallback?: string): string {
  if (err instanceof LscApiError) {
    return supportIssueErrorMessage(err.code, err.message);
  }
  if (err instanceof Error && err.message && !INTERNAL_COPY_PATTERN.test(err.message)) {
    return err.message;
  }
  return fallback ?? supportIssueErrorMessage(undefined);
}

export function getFrontendBundleId(): string | null {
  const fromEnv = (process.env.REACT_APP_BUILD_SHA || '').trim();
  if (fromEnv) return fromEnv.slice(0, 120);
  if (typeof window !== 'undefined') {
    const scripts = Array.from(document.querySelectorAll('script[src*="/static/js/main"]'));
    for (const node of scripts) {
      const src = node.getAttribute('src') || '';
      const match = src.match(/main\.([a-f0-9]+)\.js/i);
      if (match?.[1]) return `main.${match[1]}.js`;
    }
  }
  return null;
}

export function detectBrowserFamily(): string | null {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  if (/Edg\//i.test(ua)) return 'edge';
  if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) return 'chrome';
  if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) return 'safari';
  if (/Firefox\//i.test(ua)) return 'firefox';
  return 'other';
}

function sanitizeErrorCode(raw: string | undefined | null): string | null {
  const text = String(raw || '').trim().slice(0, 120);
  if (!text || INTERNAL_COPY_PATTERN.test(text)) return null;
  return text;
}

function trimContextString(value: unknown, maxLen = 512): string | null {
  const text = String(value ?? '').trim();
  if (!text || text.length > maxLen) return text ? text.slice(0, maxLen) : null;
  if (INTERNAL_COPY_PATTERN.test(text)) return null;
  return text;
}

export function sanitizeSupportIssueContext(raw: LscSupportIssueContext | null | undefined): LscSupportIssueContext {
  if (!raw || typeof raw !== 'object') return {};
  const cleaned: LscSupportIssueContext = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!SAFE_CONTEXT_KEYS.has(key) || FORBIDDEN_CONTEXT_KEY_PATTERN.test(key)) continue;
    if (key === 'billing_posture' && value && typeof value === 'object') {
      const posture = value as Record<string, unknown>;
      cleaned.billing_posture = {
        billing_model_enabled: Boolean(posture.billing_model_enabled),
        billing_checkout_enabled: Boolean(posture.billing_checkout_enabled),
        billing_enforcement_enabled: Boolean(posture.billing_enforcement_enabled),
      };
      continue;
    }
    if (typeof value === 'boolean') {
      (cleaned as Record<string, boolean>)[key] = value;
      continue;
    }
    const text = trimContextString(value);
    if (text) {
      (cleaned as Record<string, string>)[key] = text;
    }
  }
  return cleaned;
}

export interface BuildSupportIssueContextInput {
  route: string;
  surface?: string | null;
  league?: Pick<
    LscLeague,
    'active_season_label' | 'active_stage_label' | 'season_phase'
  > | null;
  errorCode?: string | null;
}

export function buildSupportIssueContext(input: BuildSupportIssueContextInput): LscSupportIssueContext {
  return sanitizeSupportIssueContext({
    route: input.route,
    surface: input.surface ?? undefined,
    frontend_bundle: getFrontendBundleId(),
    league_active_season: displaySeasonLabel(input.league?.active_season_label) || undefined,
    league_active_week: input.league?.active_stage_label ?? undefined,
    league_phase: input.league?.season_phase ?? undefined,
    error_code: sanitizeErrorCode(input.errorCode) ?? undefined,
    browser_family: detectBrowserFamily() ?? undefined,
    timestamp: new Date().toISOString(),
  });
}

export function buildSupportIssueCreatePayload(input: {
  leagueId?: string | null;
  surface?: string | null;
  category: LscSupportIssueCategory;
  severity: LscSupportIssueSeverity;
  title: string;
  description?: string;
  stepsToReproduce?: string;
  expectedBehavior?: string;
  actualBehavior?: string;
  route: string;
  league?: Pick<
    LscLeague,
    'active_season_label' | 'active_stage_label' | 'season_phase'
  > | null;
  errorCode?: string | null;
}) {
  const context = buildSupportIssueContext({
    route: input.route,
    surface: input.surface,
    league: input.league,
    errorCode: input.errorCode,
  });

  return {
    league_id: input.leagueId?.trim() || null,
    surface: input.surface?.trim() || null,
    category: input.category,
    severity: input.severity,
    title: input.title.trim(),
    description: input.description?.trim() || '',
    steps_to_reproduce: input.stepsToReproduce?.trim() || '',
    expected_behavior: input.expectedBehavior?.trim() || '',
    actual_behavior: input.actualBehavior?.trim() || '',
    context,
    attachments: [],
  };
}

export function allSupportIssueStructuredErrorsHaveSafeCopy(): boolean {
  return LSC_SUPPORT_ISSUE_STRUCTURED_ERROR_CODES.every((code) => {
    const message = supportIssueErrorMessage(code);
    return message.length > 0 && assertCustomerSafeSupportIssueCopy(message);
  });
}
